import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/marketing/lib/utils";
import { Button } from "@/marketing/components/ui/button";
import { INFLUENCER_CAMPAIGN_QUERIES } from "@/marketing/infrastructure/influencer/campaign/utils/queries";
import { CAMPAIGN_PIPELINE_QUERIES } from "@/marketing/infrastructure/influencer/campaign/pipeline/utils/queries";
import type { ScoutingSummary } from '@/marketing/models/campaignScouting';
import { CAMPAIGN_PIPELINE_KEYS } from "@/marketing/infrastructure/influencer/campaign/pipeline/utils/keys";
import type { CampaignInfluencer } from "@/marketing/models/campaignInfluencer";
import { getStageLabel } from "@/marketing/lib/campaignPipeline";
import { useAuthStore } from "@/marketing/stores/useAuthStore";
import { AdminApiDomain, getAdminApiUrl } from "@/config/ApiRouting";
import { FulfillmentAdminClient } from "@/infrastructure/FulfillmentAdminClient";
import { CampaignGoodsArrivalDialog } from "@/pages/fulfillment/components/CampaignGoodsArrivalDialog";

import { ActivityTab } from "./activity/ActivityTab";
import { AddInfluencerModal } from "./addInfluencer/AddInfluencerModal";
import { CampaignBatchManager } from "./batches/CampaignBatchManager";
import { useCampaignBatchActions } from "./batches/useCampaignBatchActions";
import { BriefAssetsTab } from "./brief/BriefAssetsTab";
import { CampaignDetailHeader } from "./CampaignDetailHeader";
import { ContentTrackingTab } from "./contentTracking/ContentTrackingTab";
import { CreatorsTab } from "./creators/CreatorsTab";
import { CampaignCSVImportDialog } from "./csvImport/CampaignCSVImportDialog";
import { NewCampaignDialog } from "../NewCampaignDialog";
import { OverviewSubgrid } from "./overview/OverviewSubgrid";
import { PipelineBoard } from "./pipeline/PipelineBoard";
import { RightRail } from "./rightRail/RightRail";
import { SettlementTab } from "./settlement/SettlementTab";
import { useCampaignPipelineDefinition } from "./pipeline/useCampaignPipelineDefinition";
import { InfluencerSlideOver } from "./slideover/InfluencerSlideOver";
import { CreatorScoreProvider } from "./score/CreatorScoreProvider";
import { ScoutingTab } from './scouting/ScoutingTab';

type DetailTab = "overview" | "creators" | "scouting" | "content" | "activity" | "settlement" | "brief";

interface CampaignDetailPageProps {
  campaignId: string;
}

const TABS: { key: DetailTab; label: string; showCount?: boolean }[] = [
  { key: "overview", label: "Overview" },
  { key: "creators", label: "Influencers", showCount: true },
  { key: "scouting", label: "Scouting" },
  { key: "content", label: "Content Tracking" },
  { key: "activity", label: "Activity & Notes" },
  { key: "settlement", label: "Settlement" },
  { key: "brief", label: "Brief & Assets" },
];

interface AddModalState {
  defaultStage?: string;
  lockStage: boolean;
}

export function CampaignDetailPage({ campaignId }: CampaignDetailPageProps) {
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get("tab");
  const tab: DetailTab = TABS.find((item) => item.key === requestedTab)?.key ?? "overview";
  const setTab = (next: DetailTab) => setParams((current) => {
    const updated = new URLSearchParams(current);
    updated.set("tab", next);
    return updated;
  });
  const queryClient = useQueryClient();
  const [scoutingSummary, setScoutingSummary] = useState<ScoutingSummary>();
  const needsProfiles = tab !== "scouting" && tab !== "content";
  const [selectedInfluencerId, setSelectedInfluencerId] = useState<string | null>(null);
  const [reviewQueue, setReviewQueue] = useState<CampaignInfluencer[]>([]);
  const [addModal, setAddModal] = useState<AddModalState | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [arrivalOpen, setArrivalOpen] = useState(false);
  // The arrival is a fulfillment write, credited to the signed-in operator the fulfillment client names (SSD-7458).
  const fulfillmentClient = useMemo(
    () => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)),
    [],
  );
  const [batchManagerOpen, setBatchManagerOpen] = useState(false);
  const batchActions = useCampaignBatchActions(campaignId);

  const currentUser = useAuthStore((s) => s.user);

  const campaignQuery = useQuery(
    INFLUENCER_CAMPAIGN_QUERIES.detail(campaignId),
  );
  const influencersQuery = useQuery({ ...CAMPAIGN_PIPELINE_QUERIES.list(campaignId), enabled: campaignId !== "" && needsProfiles });
  const selectedQuery = useQuery({ ...CAMPAIGN_PIPELINE_QUERIES.detail(campaignId, selectedInfluencerId ?? ''), enabled: tab === 'scouting' && selectedInfluencerId !== null });
  useEffect(() => {
    if (!needsProfiles) {
      void queryClient.cancelQueries({ queryKey: CAMPAIGN_PIPELINE_KEYS.list(campaignId) });
    }
  }, [needsProfiles, campaignId, queryClient]);
  useEffect(() => {
    if (tab === "content") {
      setSelectedInfluencerId(null);
      setReviewQueue([]);
      setAddModal(null);
      setBatchManagerOpen(false);
    }
  }, [tab]);
  const stockSourceQuery = useQuery(INFLUENCER_CAMPAIGN_QUERIES.stockSource(campaignId));
  // Offered where the brand sends the goods, and where the source could not be read: the server answers a campaign
  // that takes FF2 stock with nothing received, so an unread source never hides the button the desk may need.
  const receivesCampaignGoods =
    stockSourceQuery.data?.stockSource === "CAMPAIGN_SUPPLIED" || stockSourceQuery.isError;
  const pipeline = useCampaignPipelineDefinition(
    campaignQuery.data?.pipelineType,
  );
  const creatorCountsByBatch = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const creator of influencersQuery.data ?? []) {
      if (creator.batchId) counts[creator.batchId] = (counts[creator.batchId] ?? 0) + 1;
    }
    return counts;
  }, [influencersQuery.data]);

  const handleCardClick = (creator: CampaignInfluencer, visibleQueue?: CampaignInfluencer[]) => {
    const all = influencersQuery.data ?? [];
    const queue = visibleQueue ?? all.filter(
      (c) =>
        c.pipelineStage === creator.pipelineStage &&
        c.rejected === creator.rejected,
    );
    setReviewQueue(queue);
    setSelectedInfluencerId(creator.influencerId);
  };

  const handleCloseSlideover = () => {
    setSelectedInfluencerId(null);
    setReviewQueue([]);
  };

  const handleAdvanceToNext = () => {
    if (!selectedInfluencerId) return;
    const currentIdx = reviewQueue.findIndex(
      (c) => c.influencerId === selectedInfluencerId,
    );
    const next =
      currentIdx >= 0 ? reviewQueue[currentIdx + 1] : reviewQueue[0];
    if (next) {
      setSelectedInfluencerId(next.influencerId);
    } else {
      const current = reviewQueue.find((c) => c.influencerId === selectedInfluencerId);
      const stageLabel =
        current && pipeline.definition
          ? getStageLabel(pipeline.definition, current.pipelineStage)
          : "";
      toast.success(
        current?.rejected
          ? "Reviewed all rejected influencers"
          : `Reviewed all in ${stageLabel}`,
      );
      handleCloseSlideover();
    }
  };

  const openAddModalForStage = (stage: string) => {
    setAddModal({ defaultStage: stage, lockStage: true });
  };

  const openAddModalUnlocked = () => {
    setAddModal({ lockStage: false });
  };

  if (campaignQuery.isLoading) {
    return (
      <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-sm tw-text-muted-foreground">
        Loading campaign…
      </div>
    );
  }
  if (campaignQuery.isError || !campaignQuery.data) {
    return (
      <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-sm tw-text-red-600">
        Failed to load campaign.{" "}
        {campaignQuery.error instanceof Error
          ? campaignQuery.error.message
          : ""}
      </div>
    );
  }

  if (pipeline.isLoading) {
    return (
      <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-sm tw-text-muted-foreground">
        Loading pipeline…
      </div>
    );
  }
  if (!pipeline.definition) {
    return (
      <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-sm tw-text-red-600">
        Failed to load the campaign pipeline. {pipeline.errorMessage ?? ""}
      </div>
    );
  }

  const campaign = campaignQuery.data;
  const pipelineDefinition = pipeline.definition;
  const influencers = needsProfiles ? influencersQuery.data ?? [] : [];
  const selectedInfluencer = influencers.find((c) => c.influencerId === selectedInfluencerId) ?? reviewQueue.find((c) => c.influencerId === selectedInfluencerId) ?? null;
  const existingIds = influencers.map((c) => c.influencerId);

  const liveSelectedInfluencer = tab === "content" ? null : tab === 'scouting' && selectedQuery.data ? selectedQuery.data : selectedInfluencer
    ? (influencers.find((c) => c.influencerId === selectedInfluencer.influencerId) ?? selectedInfluencer)
    : null;

  const influencersError = needsProfiles && influencersQuery.isError
    ? influencersQuery.error instanceof Error
      ? influencersQuery.error.message
      : "Failed to load pipeline."
    : null;

  return (
    <CreatorScoreProvider key={campaignId} campaignId={campaignId} creators={influencers} enabled={needsProfiles} detailsEnabled={tab !== "content"}>
    <div className="tw-flex tw-h-full tw-overflow-hidden">
      {/* Main column */}
      <main className="tw-min-w-0 tw-flex-1 tw-overflow-y-auto">
        <div className="tw-sticky tw-top-0 tw-z-10 tw-bg-background">
        <CampaignDetailHeader
          campaign={campaign}
          pipelineDefinition={pipelineDefinition}
          influencers={influencers}
          scoutingSummary={scoutingSummary}
          useScoutingSummary={tab === 'scouting'}
          showInfluencerKpis={tab !== "content"}
          onJumpToTab={(target) => setTab(target)}
          onEdit={() => setEditOpen(true)}
          onGoodsArrived={receivesCampaignGoods ? () => setArrivalOpen(true) : undefined}
        />

        {influencersError && (
          <div className="tw-border-b tw-border-red-200 tw-bg-red-50 tw-px-8 tw-py-2 tw-text-xs tw-text-red-700">
            Pipeline failed to load — {influencersError}. KPIs and tabs may be
            incomplete.
          </div>
        )}

        {/* Tabs */}
        <div className="tw-flex tw-h-10 tw-items-center tw-border-b tw-border-border tw-bg-background tw-px-8">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                "tw-relative tw-inline-flex tw-h-10 tw-items-center tw-gap-1.5 tw-px-4 tw-text-sm tw-font-medium",
                tab === t.key
                  ? "tw-text-foreground"
                  : "tw-text-muted-foreground hover:tw-text-foreground",
                tab === t.key &&
                  "after:tw-absolute after:tw-bottom-0 after:tw-left-4 after:tw-right-4 after:tw-h-0.5 after:tw-rounded-full after:tw-bg-blue-600",
              )}
            >
              {t.label}
              {t.showCount && tab !== "content" && (
                <span className="tw-rounded-full tw-bg-muted tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-text-muted-foreground">
                  {tab === 'scouting' ? scoutingSummary?.enrolled ?? '…' : influencers.length}
                </span>
              )}
            </button>
          ))}
        </div>
        </div>

        {/* Tab body */}
        {tab === "overview" && (
          <>
            {/* Sub-toolbar */}
            <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-border tw-bg-background tw-px-8 tw-py-3">
              <div className="tw-text-xs tw-text-muted-foreground">
                Drag a card across stages, or click the + on a column to add
                into that stage.
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                <CampaignCSVImportDialog
                  campaignId={campaign.campaignId}
                  pipelineDefinition={pipelineDefinition}
                />
                <Button
                  size="sm"
                  className="tw-gap-1.5"
                  onClick={openAddModalUnlocked}
                >
                  <Plus className="tw-h-3.5 tw-w-3.5" />
                  Add influencer
                </Button>
              </div>
            </div>

            <PipelineBoard
              campaignId={campaign.campaignId}
              pipelineDefinition={pipelineDefinition}
              creators={influencers}
              onCardClick={handleCardClick}
              onAdd={openAddModalForStage}
            />
            <OverviewSubgrid
              campaign={campaign}
              pipelineDefinition={pipelineDefinition}
              influencers={influencers}
              onOpenBrief={() => setTab("brief")}
            />
          </>
        )}
        {tab === "creators" && (
          <CreatorsTab
            campaign={campaign}
            pipelineDefinition={pipelineDefinition}
            creators={influencers}
            onRowClick={handleCardClick}
            onAddCreator={openAddModalUnlocked}
            onMoveToBatch={batchActions.moveToBatch}
            onManageBatches={() => setBatchManagerOpen(true)}
          />
        )}
        {batchManagerOpen && (
          <CampaignBatchManager
            open
            onOpenChange={setBatchManagerOpen}
            campaign={campaign}
            creatorCounts={creatorCountsByBatch}
            onChange={batchActions.changeBatches}
          />
        )}
        {tab === 'scouting' && <ScoutingTab pipelineType={campaign.pipelineType} campaignId={campaign.campaignId} onRowClick={handleCardClick} onSummary={setScoutingSummary} />}
        {tab === "content" && (
          <ContentTrackingTab campaignId={campaign.campaignId} pipelineDefinition={pipelineDefinition} />
        )}
        {tab === "activity" && <ActivityTab campaignId={campaign.campaignId} />}
        {tab === "settlement" && (
          <SettlementTab
            campaignId={campaign.campaignId}
            pipelineDefinition={pipelineDefinition}
            influencers={influencers}
          />
        )}
        {tab === "brief" && <BriefAssetsTab campaign={campaign} />}
      </main>

      {needsProfiles && <RightRail
        campaignId={campaign.campaignId}
        campaignName={campaign.name}
      />}

      <AddInfluencerModal
        campaignId={campaign.campaignId}
        campaign={campaign}
        pipelineDefinition={pipelineDefinition}
        open={addModal !== null}
        onClose={() => setAddModal(null)}
        defaultStage={addModal?.defaultStage}
        lockStage={addModal?.lockStage ?? false}
        existingIds={existingIds}
      />

      <InfluencerSlideOver
        campaignId={campaign.campaignId}
        campaign={campaign}
        pipelineDefinition={pipelineDefinition}
        influencer={liveSelectedInfluencer}
        onClose={handleCloseSlideover}
        onAdvanceToNext={handleAdvanceToNext}
      />

      <CampaignGoodsArrivalDialog
        show={arrivalOpen}
        client={fulfillmentClient}
        scope={{ campaignId: campaign.campaignId }}
        scopeLabel={`campaign ${campaign.name}`}
        onHide={() => setArrivalOpen(false)}
      />

      <NewCampaignDialog
        open={editOpen}
        mode="edit"
        initialCampaign={campaign}
        onOpenChange={setEditOpen}
        defaultOwnerId={currentUser?.email ?? ""}
      />
    </div>
    </CreatorScoreProvider>
  );
}
